type ParameterInput = {
  id?: string;
  key: string;
  settings: string;
  inputs: Record<string, any>[];
  enabled?: boolean;
  /**
   * Marks a "Multiple" parameter as restricted. Mirrors the same field on the
   * store's ParameterInput, which is a separate declaration of this shape.
   */
  restricted?: boolean;
};

/**
 * Build the `visibility_metadata` payload for a parameter.
 *
 * This must be included on EVERY parameter branch, not just the "Multiple" one.
 * The backend writes the column only when the key is present in the request
 * (`array_key_exists`), so omitting it would leave a parameter that was switched
 * away from "Multiple (Restricted)" flagged as restricted forever, and it would
 * keep surfacing in the Ad Builder. Restricted is only meaningful for
 * multi_publisher, so every other mode explicitly clears it with `null`.
 */
function buildVisibilityMetadata(param: {
  settings?: string;
  restricted?: boolean;
}): { restricted: boolean; surface: string } | null {
  return param.settings === "multi_publisher" && param.restricted
    ? { restricted: true, surface: "ad_builder" }
    : null;
}

type ParameterDefinitionField = {
  id: string;
  key: string;
};

type ParameterDefinition = {
  id: string;
  key: string;
  fields: ParameterDefinitionField[];
};

type AdInput = {
  id?: string;
  name: string;
  mediaType: string;
};

type AdType = {
  id?: string;
  publisher: string | null;
  publisherId?: string;
  adInputs: AdInput[];
};

type CustomParameter = {
  id?: string;
  key: string;
  name: string;
  type: string;
  dropdownOptions?: string[];
  setting: string;
  values: any[];
  enabled?: boolean;
};

type HelperInput = {
  adCategory: string;
  publisherType: string;
  publishers: Array<{ id?: string; name: string }>;
  adTypes: AdType[];
  parametersInput: ParameterInput[];
  parametersData: any;
  customParameters: any;
};

type CampaignOutput = {
  name: string;
  publisher_type: string;
  publishers: string[];
  ad_types: any[];
};

type UpdateCampaignOutput = {
  name: string;
  publisher_type: string;
  publishers: Array<{ id?: string; name: string }>;
  ad_types: any[];
};

function convertToAPIFormat(helperInput: HelperInput): CampaignOutput {
  const {
    adCategory,
    publisherType,
    publishers,
    adTypes,
    parametersInput,
    parametersData,
    customParameters,
  } = helperInput;

  function findParamDef(
    mediaType: string,
    groupKey: string
  ): ParameterDefinition | undefined {
    const defs = parametersData[mediaType.toLowerCase()] || [];
    return defs.find((def) => def.key === groupKey);
  }

  function buildParametersForInput(
    publisher: string | null,
    inputName: string,
    mediaType: string
  ) {
    const prefix = publisher
      ? `${publisher}_${inputName}`
      : `null_${inputName}`;
    const matchedParams = parametersInput.filter((param) =>
      param.key.startsWith(prefix)
    );

    return matchedParams
      .map((param) => {
        const groupKey = param.key.replace(`${prefix}_`, "");
        const paramDef = findParamDef(mediaType, groupKey);
        if (!paramDef) return null;

        // If disabled, send minimal payload so backend can update is_enabled
        if (param.enabled === false) {
          const result: any = {
            group_key: groupKey,
            value_type: param.settings || "user_defined",
            fields: [],
            is_enabled: false,
            parameter_definition_id: paramDef.id,
            visibility_metadata: buildVisibilityMetadata(param),
          };
          if (param.id) result.id = param.id;
          return result;
        }

        // Treat empty settings as user_defined by default
        const settingValue = param.settings && param.settings.trim() !== "" 
          ? param.settings 
          : "user_defined";

        const inputs = param.inputs || [];
        const isUserDefined = settingValue === "user_defined";
        const isMultiPublisher = settingValue === "multi_publisher";
        const isSinglePublisher = settingValue === "single_publisher";

        if (isMultiPublisher) {
          const groupedInputs: Record<string, any[]> = {};

          inputs.forEach((inputObj) => {
            for (const key in inputObj) {
              if (!groupedInputs[key]) groupedInputs[key] = [];
              groupedInputs[key].push(inputObj[key]);
            }
          });

          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: groupedInputs[field.key] || [],
          }));

          return {
            parameter_definition_id: paramDef.id,
            group_key: groupKey,
            value_type: settingValue,
            fields,
            inputs: groupedInputs,
            is_enabled: true,
            // Mirrors the update path so a category created with restricted
            // params carries the flag from the outset.
            visibility_metadata: buildVisibilityMetadata(param),
          };
        }

        if (isUserDefined) {
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
          }));

          return {
            parameter_definition_id: paramDef.id,
            group_key: groupKey,
            value_type: settingValue,
            fields,
            inputs: {},
            is_enabled: true,
          };
        }

        if (isSinglePublisher) {
          const flatInput = inputs[0] || {};
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: flatInput[field.key] ?? null,
          }));

          return {
            parameter_definition_id: paramDef.id,
            group_key: groupKey,
            value_type: settingValue,
            fields,
            inputs: flatInput,
            is_enabled: true,
          };
        }

        const fieldsArr = inputs.map((inputObj) =>
          paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: inputObj[field.key] ?? null,
          }))
        );

        return {
          parameter_definition_id: paramDef.id,
          group_key: groupKey,
          value_type: settingValue,
          fields: fieldsArr[0] || [],
          inputs,
          is_enabled: true,
        };
      })
      .filter(Boolean);
  }

  function buildCustomParameters(
    customParams: CustomParameter[],
    publisher: string | null,
    name: string
  ) {
    const prefix = publisher ? `${publisher}_${name}_` : `null_${name}_`;

    return customParams
      .filter((cp) => cp.key.startsWith(prefix))
      .map((cp) => ({
        key: cp.key,
        name: cp.name,
        type: cp.type,
        dropdown_options: cp.dropdownOptions,
        setting: cp.setting,
        values: cp.values,
        is_enabled: cp.enabled !== false,
      }));
  }

  const ad_types = adTypes.map(({ publisher, adInputs }) => ({
    publisher,
    ad_inputs: adInputs.map(({ name, mediaType }) => ({
      media_type: mediaType,
      name,
      parameters: buildParametersForInput(publisher, name, mediaType),
      custom_parameters: buildCustomParameters(
        customParameters,
        publisher,
        name
      ),
    })),
  }));

  return {
    name: adCategory,
    publisher_type:
      publisherType === "single_publisher"
        ? "single"
        : publisherType === "multiple_publisher"
        ? "multi"
        : publisherType,
    publishers: publishers.map((e) => e.name),
    ad_types,
  };
}

function updateDataConvertToAPIFormat(
  helperInput: HelperInput
): UpdateCampaignOutput {
  const {
    adCategory,
    publisherType,
    publishers,
    adTypes,
    parametersInput,
    parametersData,
    customParameters,
  } = helperInput;

  function findParamDef(
    mediaType: string,
    groupKey: string
  ): ParameterDefinition | undefined {
    const defs = parametersData[mediaType.toLowerCase()] || [];
    return defs.find((def) => def.key === groupKey);
  }

  function buildParametersForInput(
    publisher: string | null,
    inputName: string,
    mediaType: string
  ) {
    const prefix = publisher
      ? `${publisher}_${inputName}`
      : `null_${inputName}`;
    const matchedParams = parametersInput.filter((param) =>
      param.key.startsWith(prefix)
    );

    return matchedParams
      .map((param) => {
        const groupKey = param.key.replace(`${prefix}_`, "");
        const paramDef = findParamDef(mediaType, groupKey);
        if (!paramDef) return null;

        // If disabled, send minimal payload so backend can update is_enabled
        if (param.enabled === false) {
          const result: any = {
            group_key: groupKey,
            value_type: param.settings || "user_defined",
            fields: [],
            is_enabled: false,
            parameter_definition_id: paramDef.id,
            visibility_metadata: buildVisibilityMetadata(param),
          };
          if (param.id) result.id = param.id;
          return result;
        }

        // Treat empty settings as user_defined by default
        const settingValue = param.settings && param.settings.trim() !== "" 
          ? param.settings 
          : "user_defined";

        const inputs = param.inputs || [];
        const isUserDefined = settingValue === "user_defined";
        const isMultiPublisher = settingValue === "multi_publisher";
        const isSinglePublisher = settingValue === "single_publisher";

        if (isMultiPublisher) {
          const groupedInputs: Record<string, any[]> = {};

          inputs.forEach((inputObj) => {
            for (const key in inputObj) {
              if (!groupedInputs[key]) groupedInputs[key] = [];
              // groupedInputs[key].push(Number(inputObj[key]));
              groupedInputs[key].push(inputObj[key]);

            }
          });

          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: groupedInputs[field.key] || [],
          }));

          const result: any = {
            group_key: groupKey,
            value_type: settingValue,
            fields,
            is_enabled: true,
            parameter_definition_id: paramDef.id,
          };

          result.visibility_metadata = buildVisibilityMetadata(param);

          if (param.id) result.id = param.id;

          return result;
        }

        if (isUserDefined) {
          const fields = paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: null, // Include null value for user-defined fields
          }));

          const result: any = {
            group_key: groupKey,
            value_type: settingValue,
            fields,
            is_enabled: true,
            parameter_definition_id: paramDef.id,
            visibility_metadata: buildVisibilityMetadata(param),
          };

          if (param.id) result.id = param.id;

          return result;
        }

        if (isSinglePublisher) {
          const flatInput = inputs[0] || {};
          const fields = paramDef.fields.map((field) => {
            // Always include the field, converting undefined to null
            const value = flatInput[field.key];
            return {
              parameter_field_id: field.id,
              key: field.key,
              value: value !== undefined && value !== "" ? value : null,
            };
          });

          const result: any = {
            group_key: groupKey,
            value_type: settingValue,
            fields,
            is_enabled: true,
            parameter_definition_id: paramDef.id,
            visibility_metadata: buildVisibilityMetadata(param),
          };

          if (param.id) result.id = param.id;

          return result;
        }

        const fieldsArr = inputs.map((inputObj) =>
          paramDef.fields.map((field) => ({
            parameter_field_id: field.id,
            key: field.key,
            value: inputObj[field.key] ?? null,
          }))
        );

        const result: any = {
          group_key: groupKey,
          value_type: settingValue,
          fields: fieldsArr[0] || [],
          is_enabled: true,
          parameter_definition_id: paramDef.id,
          visibility_metadata: buildVisibilityMetadata(param),
        };

        if (param.id) result.id = param.id;

        return result;
      })
      .filter(Boolean);
  }

  function buildCustomParameters(
    customParams: CustomParameter[],
    publisher: string | null,
    name: string
  ) {
    const prefix = publisher ? `${publisher}_${name}_` : `null_${name}_`;

    return customParams
      .filter((cp) => cp.key.startsWith(prefix))
      .map((cp) => {
        const result: any = {
          name: cp.name,
          type: cp.type,
          setting: cp.setting,
          values: cp.values,
          is_enabled: cp.enabled !== false,
        };

        if (cp.id) result.id = cp.id;

        if (cp.dropdownOptions) {
          result.dropdown_options = cp.dropdownOptions;
        }

        return result;
      });
  }

  const ad_types = adTypes.map(
    ({ id: adTypeId, publisher, publisherId, adInputs }) => {
      const adTypeResult: any = {
        publisher,
        ad_inputs: adInputs.map(({ id: adInputId, name, mediaType }) => {
          const adInputResult: any = {
            media_type: mediaType,
            name,
            parameters: buildParametersForInput(publisher, name, mediaType),
            custom_parameters: buildCustomParameters(
              customParameters,
              publisher,
              name
            ),
          };

          if (adInputId) adInputResult.id = adInputId;

          return adInputResult;
        }),
      };

      if (adTypeId) adTypeResult.id = adTypeId;
      if (publisherId) adTypeResult.publisher_id = publisherId;

      return adTypeResult;
    }
  );

  return {
    name: adCategory,
    publisher_type:
      publisherType === "single_publisher"
        ? "single"
        : publisherType === "multiple_publisher"
        ? "multi"
        : publisherType,
    publishers: publishers.map((p) => {
      const pub: any = { name: p.name };
      if (p.id) pub.id = p.id;
      return pub;
    }),
    ad_types,
  };
}

export { convertToAPIFormat, updateDataConvertToAPIFormat };
